/**
 * @module ol-ishare/control/historynavigation
 */

import { Control } from 'ol/control';
import { ViewHistory } from '../history.js';
import { lang } from '../lang.js';

/**
 * @extends {ol/control/Control~Control}
 * @param {Object} options Control options, extends {@link ol/control/Control} options adding:
 * @param {String} [options.backHtml=&lArr;] Text for back button
 * @param {String} [options.forwardHtml=&rArr;] Text for forward button
 * @param {String} [options.backTitle=Navigate back] Title attribute for back button
 * @param {String} [options.forwardTitle=Navigate forward] Title attribute for forward button
 * @classdesc HistoryNavigation control. Displays back and forward buttons to allow the user to navigate map center and zoom history.
 *
 * If any of the `backHtml`, `forwardHtml`, `backTitle` and `forwardTitle`
 * options are unset, the iShare server version is >= v6.0.9, your langauge
 * file contains entries under `oli.historyNavigation` and the lang UI text
 * lookup is available when this control is created then the entries from the
 * language file will be used. Otherwise the default English values will be
 * used. The lang lookup is automatically loaded when creating a LiteMap and is
 * populated once the `load` event is dispatched.
 *
 * If `backHtml` or `forwardHtml` are set to a string which is more than a character wide, consider the following CSS to allow the buttons to adapt to their text: `.oli-history.ol-control button { width: auto; }`
 * @class
 * @name HistoryNavigation
 */
class HistoryNavigation extends Control {
  constructor(options) {
    var element = document.createElement('div');
    element.className = 'oli-history ol-unselectable ol-control';
    options = Object.assign({ element: element }, options);

    var back = document.createElement('button');
    back.innerHTML =
      options.backHtml || lang?.oli?.historyNavigation?.backHtml || '&lArr;';
    back.title =
      options.backTitle ||
      lang?.oli?.historyNavigation?.backTitle ||
      'Navigate back';
    back.setAttribute('aria-label', back.title);
    back.className = 'oli-history-back';

    var forward = document.createElement('button');
    forward.innerHTML =
      options.forwardHtml ||
      lang?.oli?.historyNavigation?.forwardHtml ||
      '&rArr;';
    forward.title =
      options.forwardTitle ||
      lang?.oli?.historyNavigation?.forwardTitle ||
      'Navigate forward';
    forward.setAttribute('aria-label', forward.title);
    forward.className = 'oli-history-forward';

    element.appendChild(back);
    element.appendChild(forward);

    super({ element: element, target: options.target });
    this.disableButton_(back);
    this.disableButton_(forward);
    this.back_ = back;
    this.forward_ = forward;

    back.addEventListener('click', this.navigateBack.bind(this), false);
    forward.addEventListener('click', this.navigateForward.bind(this), false);
  }

  disableButton_(button) {
    button.disabled = true;
    button.classList.add('oli-disabled');
  }
  enableButton_(button) {
    button.disabled = false;
    button.classList.remove('oli-disabled');
  }

  navigateBack(evt) {
    if (this.getMap()) {
      this.history.back();
    }
  }
  navigateForward(evt) {
    if (this.getMap()) {
      this.history.forward();
    }
  }

  setMap(map) {
    super.setMap(map);
    var nav = this;
    this.history = new ViewHistory(this.getMap());
    this.history.on('change:history', function (evt) {
      if (evt.atEnd) {
        nav.disableButton_(nav.forward_);
      } else {
        nav.enableButton_(nav.forward_);
      }
      if (evt.atStart) {
        nav.disableButton_(nav.back_);
      } else {
        nav.enableButton_(nav.back_);
      }
    });
  }
}

export { HistoryNavigation };
